export const DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY = '__modelConfig';

export const DOCUMENT_SCAN_AI_SUPPORTED_MODELS = [
    'gemini-3-flash-preview',
    'gemini-2.5-flash',
    'gemini-2.5-flash-lite',
] as const;
export const DOCUMENT_SCAN_AI_THINKING_LEVEL_MODELS = ['gemini-3-flash-preview'] as const;
export const DOCUMENT_SCAN_AI_THINKING_LEVELS = ['MINIMAL', 'LOW', 'MEDIUM', 'HIGH'] as const;
export const DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MIN = 1;
export const DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MAX = 65536;

export type DocumentScanAiModel = (typeof DOCUMENT_SCAN_AI_SUPPORTED_MODELS)[number];
export type DocumentScanAiThinkingLevel = (typeof DOCUMENT_SCAN_AI_THINKING_LEVELS)[number];

export interface DocumentScanAiModelConfig {
    model?: DocumentScanAiModel;
    maxOutputTokens?: number;
    thinkingLevel?: DocumentScanAiThinkingLevel;
}

export function documentScanAiModelSupportsThinkingLevel(model: string | null | undefined): boolean {
    return DOCUMENT_SCAN_AI_THINKING_LEVEL_MODELS.includes(
        model as (typeof DOCUMENT_SCAN_AI_THINKING_LEVEL_MODELS)[number],
    );
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function normalizeDocumentScanAiModelConfig(value: unknown): DocumentScanAiModelConfig {
    if (value === null || value === undefined) {
        return {};
    }
    if (!isRecord(value)) {
        throw new Error('modelConfig must be an object or null');
    }

    const supportedKeys = new Set(['model', 'maxOutputTokens', 'thinkingLevel']);
    const unsupportedKeys = Object.keys(value).filter((key) => !supportedKeys.has(key));
    if (unsupportedKeys.length) {
        throw new Error(`modelConfig contains unsupported properties: ${unsupportedKeys.join(', ')}`);
    }

    const normalized: DocumentScanAiModelConfig = {};
    if (value.model !== undefined && value.model !== null && value.model !== '') {
        if (!DOCUMENT_SCAN_AI_SUPPORTED_MODELS.includes(value.model as DocumentScanAiModel)) {
            throw new Error(`modelConfig.model must be one of: ${DOCUMENT_SCAN_AI_SUPPORTED_MODELS.join(', ')}`);
        }
        normalized.model = value.model as DocumentScanAiModel;
    }
    if (value.maxOutputTokens !== undefined && value.maxOutputTokens !== null && value.maxOutputTokens !== '') {
        if (
            typeof value.maxOutputTokens !== 'number' ||
            !Number.isInteger(value.maxOutputTokens) ||
            value.maxOutputTokens < DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MIN ||
            value.maxOutputTokens > DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MAX
        ) {
            throw new Error(
                `modelConfig.maxOutputTokens must be an integer from ${DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MIN} to ${DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MAX}`,
            );
        }
        normalized.maxOutputTokens = value.maxOutputTokens;
    }
    if (value.thinkingLevel !== undefined && value.thinkingLevel !== null && value.thinkingLevel !== '') {
        if (!DOCUMENT_SCAN_AI_THINKING_LEVELS.includes(value.thinkingLevel as DocumentScanAiThinkingLevel)) {
            throw new Error(`modelConfig.thinkingLevel must be one of: ${DOCUMENT_SCAN_AI_THINKING_LEVELS.join(', ')}`);
        }
        normalized.thinkingLevel = value.thinkingLevel as DocumentScanAiThinkingLevel;
    }
    if (normalized.model && normalized.thinkingLevel && !documentScanAiModelSupportsThinkingLevel(normalized.model)) {
        throw new Error(`modelConfig.thinkingLevel is not supported by model ${normalized.model}`);
    }

    return normalized;
}

export function sanitizeDocumentScanAiModelConfig(value: unknown): DocumentScanAiModelConfig {
    if (!isRecord(value)) {
        return {};
    }

    const sanitized: DocumentScanAiModelConfig = {};
    if (DOCUMENT_SCAN_AI_SUPPORTED_MODELS.includes(value.model as DocumentScanAiModel)) {
        sanitized.model = value.model as DocumentScanAiModel;
    }
    if (
        typeof value.maxOutputTokens === 'number' &&
        Number.isInteger(value.maxOutputTokens) &&
        value.maxOutputTokens >= DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MIN &&
        value.maxOutputTokens <= DOCUMENT_SCAN_AI_MAX_OUTPUT_TOKENS_MAX
    ) {
        sanitized.maxOutputTokens = value.maxOutputTokens;
    }
    if (DOCUMENT_SCAN_AI_THINKING_LEVELS.includes(value.thinkingLevel as DocumentScanAiThinkingLevel)) {
        sanitized.thinkingLevel = value.thinkingLevel as DocumentScanAiThinkingLevel;
    }
    if (sanitized.model && sanitized.thinkingLevel && !documentScanAiModelSupportsThinkingLevel(sanitized.model)) {
        delete sanitized.thinkingLevel;
    }

    return sanitized;
}

export function assertCaptureConfigDoesNotContainModelConfig(value: unknown, fieldName = 'captureConfig'): void {
    if (isRecord(value) && Object.prototype.hasOwnProperty.call(value, DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY)) {
        throw new Error(`${fieldName} cannot include reserved property ${DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY}`);
    }
}

export function extractDocumentScanAiModelConfig(value: unknown): DocumentScanAiModelConfig {
    if (!isRecord(value) || !Object.prototype.hasOwnProperty.call(value, DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY)) {
        return {};
    }
    return sanitizeDocumentScanAiModelConfig(value[DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY]);
}

export function extractDocumentScanAiModelConfigStrict(value: unknown): DocumentScanAiModelConfig {
    if (!isRecord(value) || !Object.prototype.hasOwnProperty.call(value, DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY)) {
        return {};
    }
    return normalizeDocumentScanAiModelConfig(value[DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY]);
}

export function stripDocumentScanAiModelConfig<T extends object>(value: T | null | undefined): T {
    if (!isRecord(value)) {
        return {} as T;
    }
    const { [DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY]: _modelConfig, ...captureConfig } = value;
    return captureConfig as T;
}

export function storeDocumentScanAiModelConfig(
    captureConfig: object | null | undefined,
    modelConfig: DocumentScanAiModelConfig,
): object {
    const cleanCaptureConfig = stripDocumentScanAiModelConfig(captureConfig);
    if (!Object.keys(modelConfig).length) {
        return cleanCaptureConfig;
    }
    return {
        ...cleanCaptureConfig,
        [DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY]: modelConfig,
    };
}

export function preserveStoredDocumentScanAiModelConfig(captureConfig: object, storedCaptureConfig: unknown): object {
    const cleanCaptureConfig = stripDocumentScanAiModelConfig(captureConfig);
    if (
        !isRecord(storedCaptureConfig) ||
        !Object.prototype.hasOwnProperty.call(storedCaptureConfig, DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY)
    ) {
        return cleanCaptureConfig;
    }
    return {
        ...cleanCaptureConfig,
        [DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY]: storedCaptureConfig[DOCUMENT_SCAN_AI_MODEL_CONFIG_CAPTURE_KEY],
    };
}
